import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDrag, useDrop } from 'react-dnd'
import { User } from 'lucide-react'
import { toast } from '@/components/ui/use-toast'

// Types
import {
  TimetableAssignment,
  UnscheduledSubject,
  AvailableSlot,
  TimetableData,
} from '@/types/timetable'
import { ItemTypes } from '../UnscheduleZone'
import {
  addAssignmentToTimetableData,
  removeAssignmentFromTimetableData,
} from '@/services/timetableService'
import { checkScheduleConflicts } from './room'

interface DroppableCellProps {
  children: React.ReactNode
  day: string
  time: string
  selectedClass: string // Add this line
  selectedUnscheduledSubject: UnscheduledSubject | null
  handleRescheduleUnscheduledSubject: (
    subject: UnscheduledSubject,
    day: string,
    time: string,
    room?: string
  ) => void
  setSelectedUnscheduledSubject: (subject: UnscheduledSubject | null) => void
  getDisplayData: () => TimetableData
  setInjectedData: (data: TimetableData) => void
}

interface DraggableLessonProps {
  lesson: any
  day: string
  time: string
  selectedClass: string
  getSubjectColor: (subject: string | null | undefined) => string | null
  getContrastColor: (hexColor: string | null) => string
  handleUnscheduleLesson: (assignment: TimetableAssignment) => void
  onEditRoom?: (lesson: any, day: any, time: any) => void
}

export const DroppableCell = ({
  children,
  day,
  time,
  selectedClass, // Add this to destructured props
  selectedUnscheduledSubject,
  handleRescheduleUnscheduledSubject,
  setSelectedUnscheduledSubject,
  getDisplayData,
  setInjectedData,
}: DroppableCellProps) => {
  const { t } = useTranslation('timetable')
  // Check if this cell is an available slot for the selected unscheduled subject
  const isAvailableSlot = selectedUnscheduledSubject
    ? selectedUnscheduledSubject.available_slots.some(
        (slot: AvailableSlot) => slot.day === day && slot.time === time
      )
    : false

  // Memoize the drop callback to prevent unnecessary re-renders
  // In DragAndDrop.tsx, update the handleDrop function:
  const handleDrop = useCallback(
    (item: {
      subject?: UnscheduledSubject
      assignment?: TimetableAssignment
    }) => {
   // console.logDropped item in cell:', item, 'day:', day, 'time:', time)

      const currentData = getDisplayData()

      // Check for conflicts first
      if (item.assignment) {
        // For moving existing lessons
        const conflict = checkScheduleConflicts(
          currentData,
          day,
          time,
          String(item.assignment.teacherID),
          item.assignment.salle || '', // Use current room if not specified
          selectedClass
        )

        if (conflict) {
          toast({
            title: t('logic.dragAndDrop.schedulingConflict'),
            description: conflict.message,
            variant: 'destructive',
          })
          return // Prevent the drop if there's a conflict
        }
      } else if (item.subject) {
        // For scheduling unscheduled subjects
        let room: string | undefined
        const availableSlot = item.subject.available_slots.find(
          (slot: AvailableSlot) => slot.day === day && slot.time === time
        )

        if (availableSlot && availableSlot.available_rooms.length > 0) {
          room = availableSlot.available_rooms[0]
        }

        const conflict = checkScheduleConflicts(
          currentData,
          day,
          time,
          String(item.subject.teacherID),
          room || '', // Use first available room if not specified
          item.subject.class
        )

        if (conflict) {
          toast({
            title: t('logic.dragAndDrop.schedulingConflict'),
            description: conflict.message,
            variant: 'destructive',
          })
          return // Prevent the drop if there's a conflict
        }

        // If no conflict, proceed with scheduling
        handleRescheduleUnscheduledSubject(item.subject, day, time, room)
        setSelectedUnscheduledSubject(null)
        return
      }

      // Rest of your existing drop handling code...
      // (for moving assignments)
      if (item.assignment) {
        const updatedData = removeAssignmentFromTimetableData(
          currentData,
          item.assignment
        )

        const newAssignment: TimetableAssignment = {
          ...item.assignment,
          day,
          time,
        }

        const finalData = addAssignmentToTimetableData(
          updatedData,
          newAssignment,
          newAssignment.salle
        )

        setInjectedData(finalData)
        toast({
          title: t('logic.dragAndDrop.lessonMoved'),
          description: t('logic.dragAndDrop.lessonMovedDescription', {
            subject: newAssignment.subject,
            day,
            time,
          }),
        })
      }

      return { day, time }
    },
    [
      day,
      time,
      getDisplayData,
      handleRescheduleUnscheduledSubject,
      setInjectedData,
      setSelectedUnscheduledSubject,
      selectedClass,
    ]
  )

  // Use the useDrop hook from react-dnd with memoized callback
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: [ItemTypes.UNSCHEDULED_SUBJECT, ItemTypes.ASSIGNMENT],
      drop: handleDrop,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    }),
    [handleDrop]
  )

  return (
    <div
      ref={drop}
      className={`w-full h-full ${isOver ? 'bg-blue-100' : ''} ${isAvailableSlot ? 'bg-green-200 border-2 border-green-500' : ''}`}
      style={{ minHeight: '60px' }}
    >
      {children}
    </div>
  )
}

export const DraggableLesson = ({
  lesson,
  day,
  time,
  selectedClass,
  getSubjectColor,
  // getContrastColor,
  handleUnscheduleLesson,
}: DraggableLessonProps) => {
  // Skip if no lesson or if it's a group lesson
  if (!lesson || lesson.group1 || lesson.group2) {
    return (
      <div
        className={`rounded-md p-2 ${!lesson ? 'h-12 flex items-center justify-center text-xs text-gray-400' : ''}`}
        style={{
          backgroundColor: lesson?.subject
            ? `${getSubjectColor(lesson.subject) || '#f5f5f5'}40` // 40 is hex for 25% opacity
            : 'rgba(245, 245, 245, 0.5)',
        }}
      >
        {lesson ? (
          <>
            <div className="font-semibold text-sm">
              {lesson.subject || 'Free Period'}
            </div>
            {(lesson.teacher || lesson.teacherId || lesson.teacherID) && (
              <div className="text-xs text-gray-600 flex items-center mt-1">
                <User className="h-3 w-3 mr-1" />
                {lesson.teacher ||
                  `Teacher ${lesson.teacherId || lesson.teacherID || ''}`}
              </div>
            )}
            {(lesson.salle || lesson.classroomId) && (
              <div className="text-xs text-gray-600">
                {lesson.salle || lesson.classroomId}
              </div>
            )}
          </>
        ) : (
          <div className="text-center">
            <span className="text-xs text-gray-400"></span>
          </div>
        )}
      </div>
    )
  }

  // Create a TimetableAssignment from the lesson
  const assignment: TimetableAssignment = {
    ...lesson,
    day,
    time,
    class: selectedClass,
  }

  // Use the useDrag hook from react-dnd with useCallback to prevent recreation on every render
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: ItemTypes.ASSIGNMENT,
      item: { assignment },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      // end: (item, monitor) => {
     // console.logdrag ended', item, monitor)
        // This gets called when the drag ends
        // if (!monitor.didDrop()) {
          // If the item was not dropped on a valid drop target
          // Don't do anything here to prevent infinite loops
       // console.logDrag ended without dropping')
        // }
      // },
    }),
    [assignment] // Only recreate when assignment changes
  )

  // Use a simple click handler that doesn't trigger state updates that could cause loops
  const handleClick = () => {
    // Only unschedule if it's a direct click, not a drag operation
    if (!isDragging) {
      handleUnscheduleLesson(assignment)
    }
  }

  return (
    <div
      ref={drag}
      className="cursor-move rounded-md p-2"
      style={{
        backgroundColor: lesson.subject
          ? `${getSubjectColor(lesson.subject) || '#f5f5f5'}40` // 40 is hex for 25% opacity
          : 'rgba(245, 245, 245, 0.5)',
        opacity: isDragging ? 0.5 : 1,
      }}
      onClick={handleClick}
    >
      <div className="font-semibold text-sm">
        {lesson.subject || 'Free Period'}
      </div>
      {(lesson.teacher || lesson.teacherId || lesson.teacherID) && (
        <div className="text-xs text-gray-600 flex items-center mt-1">
          <User className="h-3 w-3 mr-1" />
          {lesson.teacher ||
            `Teacher ${lesson.teacherId || lesson.teacherID || ''}`}
        </div>
      )}
      {(lesson.salle || lesson.classroomId) && (
        <div className="text-xs text-gray-600">
          {lesson.salle || lesson.classroomId}
        </div>
      )}
    </div>
  )
}
